import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * "Compre o kit todo" — adds the 2 product-card blocks of
 * sections/product-kit.liquid to the cart in a single request.
 *
 * The parent section can't read a theme block's settings (block.settings.product
 * reads blank there, block.type reads "@theme"), so the "De R$ X" total and the
 * compact variant `<select>` per card can't be built server-side either. Both are
 * built here instead, from the `<script data-product-card-info>` that
 * blocks/product-card.liquid renders next to each card with its own product's
 * price/variants — the sanctioned way to read a theme block's data from a parent
 * is the rendered DOM, not Liquid introspection.
 */
class ProductKit extends Component {
  requiredRefs = ['addButton', 'cardsContainer', 'variantsContainer', 'originalPrice'];

  connectedCallback() {
    super.connectedCallback();
    this.#buildSummary();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#buildSummary();
  }

  /** @returns {{ price: number, hasOnlyDefaultVariant: boolean, defaultVariantId: number, variants: { id: number, title: string, available: boolean }[] }[]} */
  #getCardsInfo() {
    const cards = Array.from(this.refs.cardsContainer.querySelectorAll(':scope > product-card'));

    return cards
      .map((card) => {
        const productId = card.getAttribute('data-product-id');
        const script = this.refs.cardsContainer.querySelector(
          `script[data-product-card-info][data-for="${productId}"]`
        );

        if (!script?.textContent) return null;

        try {
          return JSON.parse(script.textContent);
        } catch {
          return null;
        }
      })
      .filter((info) => info !== null);
  }

  #buildSummary() {
    const infos = this.#getCardsInfo();
    const { variantsContainer, originalPrice, addButton } = this.refs;

    variantsContainer.replaceChildren();

    if (infos.length < 2) {
      addButton.setAttribute('disabled', 'true');
      return;
    }

    addButton.removeAttribute('disabled');

    const total = infos.reduce((sum, info) => sum + info.price, 0);
    originalPrice.textContent = formatMoney(total, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? '');

    for (const info of infos) {
      variantsContainer.append(this.#buildVariantSlot(info));
    }
  }

  /**
   * @param {{ hasOnlyDefaultVariant: boolean, defaultVariantId: number, variants: { id: number, title: string, available: boolean }[] }} info
   * @returns {HTMLElement}
   */
  #buildVariantSlot(info) {
    const slot = document.createElement('div');
    slot.className = 'product-kit__variant-slot';
    slot.setAttribute('data-kit-card', '');
    slot.setAttribute('data-default-variant-id', String(info.defaultVariantId));

    if (info.hasOnlyDefaultVariant) return slot;

    const label = document.createElement('label');
    label.className = 'product-kit__variant-label';

    const hint = document.createElement('span');
    hint.className = 'visually-hidden';
    hint.textContent = 'Escolha uma opção';
    label.append(hint);

    const select = document.createElement('select');
    select.className = 'product-kit__variant-select';
    select.setAttribute('data-kit-variant-select', '');

    for (const variant of info.variants) {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent = variant.available ? variant.title : `${variant.title} — Indisponível`;
      option.disabled = !variant.available;
      option.selected = variant.id === info.defaultVariantId;
      select.append(option);
    }

    label.append(select);
    slot.append(label);

    return slot;
  }

  /** @param {Event} event */
  addKit(event) {
    event.preventDefault();

    const { addButton, errorMessage } = this.refs;
    const cards = Array.from(this.querySelectorAll('[data-kit-card]'));

    /** @type {{ id: string, quantity: number }[]} */
    const items = [];

    for (const card of cards) {
      const select = card.querySelector('[data-kit-variant-select]');
      const variantId = select instanceof HTMLSelectElement ? select.value : card.getAttribute('data-default-variant-id');

      if (!variantId) {
        this.#showError('Um dos produtos do kit está indisponível no momento.');
        return;
      }

      items.push({ id: variantId, quantity: 1 });
    }

    if (items.length === 0) return;

    this.#hideError();
    addButton.setAttribute('disabled', 'true');

    fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('json'),
      body: JSON.stringify({ items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })) }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.message || 'Add to cart failed');
        }

        return this.#refreshCart();
      })
      .then(() => {
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product-kit',
            lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
            promise: Promise.resolve({ detail: { didError: false } }),
          })
        );
      })
      .catch((error) => {
        console.error(error);
        this.#showError('Não foi possível adicionar o kit à sacola. Tente novamente.');

        try {
          this.dispatchEvent(new CartErrorEvent({ error: error?.message, code: 'INVALID' }));
        } catch {
          // CartErrorEvent shape mismatch shouldn't hide the real error above.
        }
      })
      .finally(() => {
        addButton.removeAttribute('disabled');
      });
  }

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent && typeof (/** @type {any} */ (cartItemsComponent).fetchCartData) === 'function') {
      return /** @type {any} */ (cartItemsComponent).fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  }

  /** @param {string} message */
  #showError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    errorMessage.textContent = message;
    errorMessage.hidden = false;
  }

  #hideError() {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    errorMessage.hidden = true;
  }
}

customElements.define('product-kit-component', ProductKit);
